import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { API } from '@/App';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { BookOpen, Clock, Eye, ArrowLeft, Tag } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useIsIOSApp } from '@/hooks/useIsIOSApp';

const CATEGORY_ICONS = {
  relocation: '🏠',
  lifestyle: '🌟',
  finance: '💰',
  culture: '🎭',
  work: '💼',
  family: '👨‍👩‍👧'
};

const Articles = () => {
  const { slug } = useParams();
  const isIOSApp = useIsIOSApp();
  const [articles, setArticles] = useState([]);
  const [article, setArticle] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories();
    if (slug) {
      fetchArticle(slug);
    } else {
      fetchArticles();
    }
  }, [slug, selectedCategory]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/article-categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories');
    }
  };

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const params = selectedCategory !== 'all' ? `?category=${selectedCategory}` : '';
      const response = await axios.get(`${API}/articles${params}`);
      setArticles(response.data);
    } catch (error) {
      console.error('Failed to fetch articles');
    } finally {
      setLoading(false);
    }
  };

  const fetchArticle = async (articleSlug) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/articles/${articleSlug}`);
      setArticle(response.data);
    } catch (error) {
      console.error('Failed to fetch article');
    } finally {
      setLoading(false);
    }
  };

  // Single Article View
  if (slug && article) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />

        <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Link to="/guides">
                <Button variant="ghost" className="mb-6">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Resources
                </Button>
              </Link>

              <article>
                <div className="mb-6">
                  <span className="inline-flex items-center gap-2 px-3 py-1 bg-accent/10 text-accent rounded-full text-sm font-medium mb-4">
                    {CATEGORY_ICONS[article.category]} {article.category}
                  </span>
                  <h1 className="text-4xl font-heading font-bold mb-4">{article.title}</h1>
                  <div className="flex items-center gap-4 text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Eye className="w-4 h-4" />
                      {article.views} views
                    </span>
                    <span>By {article.author_name}</span>
                  </div>
                </div>

                {article.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-8">
                    {article.tags.map((tag) => (
                      <span key={tag} className="px-3 py-1 bg-muted rounded-full text-sm flex items-center gap-1">
                        <Tag className="w-3 h-3" />
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                <div className="prose prose-lg max-w-none prose-headings:font-bold prose-strong:font-bold prose-p:mb-6 prose-p:leading-relaxed prose-headings:mt-10 prose-headings:mb-4 prose-li:mb-2 prose-ul:my-6 prose-ol:my-6">
                  <ReactMarkdown 
                    remarkPlugins={[remarkGfm]}
                    components={{
                      p: ({node, children, ...props}) => (
                        <p {...props} className="mb-6 leading-relaxed text-gray-700">
                          {children}
                        </p>
                      ),
                      h2: ({node, children, ...props}) => (
                        <h2 {...props} className="text-2xl font-bold mt-10 mb-4 text-gray-900">
                          {children}
                        </h2>
                      ),
                      h3: ({node, children, ...props}) => (
                        <h3 {...props} className="text-xl font-bold mt-8 mb-3 text-gray-900">
                          {children}
                        </h3>
                      ),
                      ul: ({node, children, ...props}) => (
                        <ul {...props} className="my-6 space-y-3 list-disc pl-6">
                          {children}
                        </ul>
                      ),
                      ol: ({node, children, ...props}) => (
                        <ol {...props} className="my-6 space-y-3 list-decimal pl-6">
                          {children}
                        </ol>
                      ),
                      input: ({node, ...props}) => (
                        <input 
                          type="checkbox" 
                          {...props} 
                          className="mr-2 w-4 h-4 accent-primary"
                          onChange={() => {}}
                        />
                      ),
                      li: ({node, children, ...props}) => {
                        const hasCheckbox = node.children && node.children[0]?.tagName === 'input';
                        return (
                          <li {...props} className={hasCheckbox ? 'list-none flex items-start gap-1 mb-3' : 'mb-3 leading-relaxed'}>
                            {children}
                          </li>
                        );
                      },
                      blockquote: ({node, children, ...props}) => (
                        <blockquote {...props} className="border-l-4 border-primary pl-6 my-6 italic text-gray-600 bg-gray-50 py-4 pr-4 rounded-r-lg">
                          {children}
                        </blockquote>
                      )
                    }}
                  >
                    {article.content}
                  </ReactMarkdown>
                </div>
              </article>

              <div className="mt-12 p-6 bg-accent/10 rounded-2xl text-center">
                <h3 className="text-xl font-bold mb-2">Found this helpful?</h3>
                <p className="text-muted-foreground mb-4">Join ExpatsSphere to connect with expats who've been there!</p>
                <Link to={isIOSApp ? "/events" : "/membership"}>
                  <Button className="bg-primary rounded-full">
                    {isIOSApp ? 'Explore Events' : 'Join Our Community'}
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    );
  }

  // Articles List View
  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Header */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-accent via-accent to-primary rounded-full mb-6 shadow-lg">
                <BookOpen className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">
                Resources for Expat Life
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Tips, advice, and insights to help you thrive abroad. Written by expats, for expats.
              </p>
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap justify-center gap-3 mb-10">
              <Button
                variant={selectedCategory === 'all' ? 'default' : 'outline'}
                onClick={() => setSelectedCategory('all')}
                className="rounded-full"
              >
                All Guides
              </Button>
              {categories.map((cat) => (
                <Button
                  key={cat.id}
                  variant={selectedCategory === cat.id ? 'default' : 'outline'}
                  onClick={() => setSelectedCategory(cat.id)}
                  className="rounded-full"
                >
                  {cat.icon} {cat.name}
                </Button>
              ))}
            </div>

            {/* Articles Grid */}
            {loading ? (
              <div className="text-center py-20">
                <div className="text-lg text-muted-foreground">Loading guides...</div>
              </div>
            ) : articles.length === 0 ? (
              <div className="text-center py-20">
                <BookOpen className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
                <p className="text-muted-foreground">No guides found in this category yet.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {articles.map((article, index) => (
                  <motion.div
                    key={article.slug}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                  >
                    <Link to={`/guides/${article.slug}`}>
                      <div className="bg-white rounded-3xl border-2 border-border p-6 hover:shadow-lg transition-all h-full flex flex-col">
                        <div className="flex items-center gap-2 mb-4">
                          <span className="text-2xl">{CATEGORY_ICONS[article.category]}</span>
                          <span className="text-sm text-muted-foreground capitalize">{article.category}</span>
                        </div>
                        <h3 className="text-xl font-heading font-bold mb-3 line-clamp-2">{article.title}</h3>
                        <p className="text-muted-foreground text-sm flex-1 line-clamp-3">{article.excerpt}</p>
                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                          <span className="text-sm text-muted-foreground flex items-center gap-1">
                            <Eye className="w-4 h-4" />
                            {article.views || 0} views
                          </span>
                          <span className="text-primary font-medium text-sm">Read more →</span>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Articles;
